import * as React from 'react'
import { connect } from 'react-redux'
import * as ReactStrap from 'reactstrap'
import * as Common from '../common'
import * as Utils from '../common/utils'
import * as IsActive from '../../components/is_active'
import * as Actions from './actions'
import { WrappedTopicPreferences } from './reducer'
import * as queryString from 'query-string'
import * as History from 'history'

interface DispatchProps {
  onTopicPreferenceChange: (id: string) => void;
  addCustomTopics: (topicNames: string[]) => void;
  submitTopicPreferences: (communityIdentifier: Common.CommunityIdentifier, guid: string, topics: Actions.TopicPreference[]) => void;
  fetchUserTopics: (communityIdentifier: Common.CommunityIdentifier, guid: string) => void;
  fetchSuggestedTopics: (communityIdentifier: Common.CommunityIdentifier, guid: string) => void;
}

// No need for OwnProps as router data comes from Redux state
type Props = DispatchProps & WrappedTopicPreferences;

const getTopicCardStyle = (configurations: any, isActive: boolean = false, isNewlyAdded: boolean = false) => {
  const cardSize = Utils.keyValue(configurations, 'topic_preferences_card_size', 'large');
  const borderColor = Utils.keyValue(configurations, 'topic_preferences_border_color', '#dee2e6');
  const activeBorderColor = Utils.keyValue(configurations, 'topic_preferences_active_border_color', '#7FAE1B');
  
  const baseStyle = {
    transition: 'all 0.3s ease',
    borderRadius: '8px',
    border: `1px solid ${isActive ? activeBorderColor : borderColor}`,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
    height: cardSize === 'small' ? 'auto' : '100%',
    minHeight: cardSize === 'small' ? '80px' : '120px',
    backgroundColor: isActive ? '#f8f9fa' : '#ffffff',
  };

  if (isNewlyAdded && isActive) {
    return {
      ...baseStyle,
      borderColor: '#17a2b8',
      backgroundColor: '#e6f7ff',
      position: 'relative' as const,
      animation: 'pulse 2s ease-in-out',
    };
  }

  return baseStyle;
};

const topicCardStyle = {
  transition: 'all 0.3s ease',
  borderRadius: '8px',
  border: '1px solid #dee2e6',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
  height: '100%',
};

const topicCardActiveStyle = {
  ...topicCardStyle,
  borderColor: '#7FAE1B',
  backgroundColor: '#f8f9fa',
};

const topicCardNewlyAddedStyle = {
  ...topicCardActiveStyle,
  borderColor: '#17a2b8',
  backgroundColor: '#e6f7ff',
  position: 'relative' as const,
  animation: 'pulse 2s ease-in-out',
};

const topicCardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '0px',
  gap: '12px',
};

const topicCardTitleStyle = {
  margin: 0,
  fontWeight: 500,
  color: '#343a40',
  flex: 1,
  minWidth: 0, // Allows text to wrap/truncate if needed
};

const TopicCard = ({ topic, onChange, configurations }: { topic: Actions.TopicPreference; onChange: (id: string) => void; configurations: any }) => {
  const cardSize = Utils.keyValue(configurations, 'topic_preferences_card_size', 'large');
  const cardStyle = getTopicCardStyle(configurations, topic.isActive, topic.isNewlyAdded);
  
  const colSize = cardSize === 'small' ? { md: "3", sm: "4", xs: "6" } : { md: "4", sm: "6" };
  const bodyPadding = cardSize === 'small' ? '0.75rem' : '1rem';

  return (
    <ReactStrap.Col {...colSize} className="mb-4">
      <ReactStrap.Card style={cardStyle}>
        <ReactStrap.CardBody style={{ padding: bodyPadding }}>
          {topic.isNewlyAdded && topic.isActive && (
            <div style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#17a2b8',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              zIndex: 1,
            }}>
              ✓
            </div>
          )}
          <div style={topicCardHeaderStyle}>
            <h5 style={{
              ...topicCardTitleStyle,
              color: (topic.isNewlyAdded && topic.isActive) ? '#17a2b8' : topicCardTitleStyle.color
            }}>
              {(topic.isNewlyAdded && topic.isActive) && '🆕 '}{topic.name}
            </h5>
            <IsActive.UncontrolledToggleButton isActive={topic.isActive} onChange={() => onChange(topic.id)} />
          </div>
          {topic.isNewlyAdded && topic.isActive && (
            <small style={{ color: '#17a2b8', fontStyle: 'italic' }}>
              Just added to your topics
            </small>
          )}
        </ReactStrap.CardBody>
      </ReactStrap.Card>
    </ReactStrap.Col>
  );
};

const sectionStyle = {
  marginBottom: '3rem',
  padding: '2rem',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
  border: '1px solid #f0f0f0',
};

const getSectionStyle = (configurations: any, borderColor: string = '#7FAE1B') => ({
  ...sectionStyle,
  borderLeft: `4px solid ${Utils.keyValue(configurations, 'topic_preferences_active_border_color', borderColor)}`,
});

const currentTopicsSectionStyle = {
  ...sectionStyle,
  borderLeft: '4px solid #7FAE1B',
};

const recommendedSectionStyle = {
  ...sectionStyle,
  borderLeft: '4px solid #ffc107',
};

const customTopicsSectionStyle = {
  ...sectionStyle,
  borderLeft: '4px solid #17a2b8',
};

const sectionHeaderStyle = {
  marginBottom: '1.5rem',
};

const sectionDescriptionStyle = {
  color: '#6c757d',
  marginBottom: '1.5rem',
  lineHeight: 1.5,
};

const topicsContainerStyle = {
  marginTop: '1rem',
};

const saveButtonContainerStyle = {
  marginTop: '2rem',
  textAlign: 'center' as const,
};

const saveButtonStyle = {
  padding: '0.75rem 2rem',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  borderRadius: '4px',
  transition: 'all 0.2s ease',
};

const needsHeader = (topicPreferences: any): boolean => {
  return Utils.isTruthy(Utils.keyValue(topicPreferences.configurations, 'topic_preferences_header_show', 'true'));
};

const HeaderComponent = ({configurations}: any) => {
  const headerImageUrl = Utils.keyValue(configurations, 'topic_preferences_header_image_url', '') ||
                         Utils.keyValue(configurations, 'top_articles_header_image_url', '') ||
                         Utils.keyValue(configurations, 'brand_header_image_url', '');
  const headerText = Utils.keyValue(configurations, 'topic_preferences_header_text', '') ||
                    Utils.keyValue(configurations, 'top_articles_header_text', '');

  return  <ReactStrap.Navbar className="rasa-public-header">
    <ReactStrap.Nav className="center">
      <ReactStrap.NavItem className="center">
        {headerImageUrl && <img className="brand-header" src={headerImageUrl}/>}
        {headerText && <div className="brand-header-text"
          dangerouslySetInnerHTML={{__html: headerText}}>
        </div>}
      </ReactStrap.NavItem>
    </ReactStrap.Nav>
  </ReactStrap.Navbar>
};

const LoadingCard = () => (
  <ReactStrap.Col md="4" sm="6" className="mb-4">
    <ReactStrap.Card style={topicCardStyle}>
      <ReactStrap.CardBody>
        <div style={topicCardHeaderStyle}>
          <div style={{ width: '80%', height: '1.5rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
          <div style={{ width: '40px', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '34px' }}></div>
        </div>
      </ReactStrap.CardBody>
    </ReactStrap.Card>
  </ReactStrap.Col>
);

class TopicPreferences extends React.Component<Props> {
  componentDidMount() {
    // Epic handles the API calls automatically when route is entered
  }

  handleAddCustomTopics = (inputValue: string) => {
    if (!inputValue || !inputValue.trim()) {
      return;
    }

    // Parse comma-separated topics and clean them
    const topicNames = inputValue
      .split(',')
      .map(topic => topic.trim())
      .filter(topic => topic.length > 0);

    if (topicNames.length > 0) {
      this.props.addCustomTopics(topicNames);
    }
  };

  render() {
    const { topicPreferences, onTopicPreferenceChange, submitTopicPreferences } = this.props;

    // URL parameters are now handled by the epic and stored in state
    const guid = topicPreferences.guid || '';
    const communityIdentifier = topicPreferences.identifier || '';

    return (
  <ReactStrap.Container style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
    <ReactStrap.Row>
      <ReactStrap.Col>
        {needsHeader(topicPreferences) && <HeaderComponent configurations={topicPreferences.configurations} />}
        <header className="page-header text-center" style={{
          marginBottom: '1rem',
          fontSize: '2.5rem',
          fontWeight: 'bold',
          color: '#2c3e50'
        }}>
          {Utils.keyValue(topicPreferences.configurations, 'topic_preferences_page_title', 'Topic Preferences')}
        </header>
        <div className="lead-text text-center" style={{
          color: '#6c757d',
          marginBottom: '3rem',
          lineHeight: 1.6,
          fontSize: '1.2rem',
          maxWidth: '600px',
          margin: '0 auto 3rem'
        }}
        dangerouslySetInnerHTML={{
          __html: Utils.keyValue(topicPreferences.configurations, 'topic_preferences_header_description', 
                  'Personalize your newsletter experience by selecting topics that matter to you. Choose from our recommendations or add your own custom topics.')
        }}>
        </div>
      </ReactStrap.Col>
    </ReactStrap.Row>

    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
      <div className="d-flex align-items-center mb-2">
        <span className="mr-2" role="img" aria-label="info" style={{ fontSize: '1.2rem' }}>ℹ️</span>
        <span style={{ fontWeight: 500 }}>How it works:</span>
      </div>
      <p className="mb-0">Toggle the switch next to each topic to customize your content. When a topic is active, you'll receive related content in your newsletter.</p>
    </div>

    {topicPreferences.errored && (
      <ReactStrap.Alert color="danger" className="mb-4">
        {topicPreferences.errorMessage || 'An error occurred. Please try again.'}
      </ReactStrap.Alert>
    )}

    {topicPreferences.submitted && !topicPreferences.is_submitting && !topicPreferences.errored && (
      <ReactStrap.Alert color="success" className="mb-4">
        Your topic preferences have been saved successfully!
      </ReactStrap.Alert>
    )}


    <div style={getSectionStyle(topicPreferences.configurations, '#7FAE1B')}>
      <div style={sectionHeaderStyle}>
        <header className="section-header-grey">Your Current Topics</header>
        <div style={sectionDescriptionStyle}>
          Topics you are currently subscribed to receive in your newsletter:
        </div>
      </div>
      <ReactStrap.Row style={topicsContainerStyle}>
        {topicPreferences.isLoadingTopics ? (
          <>
            <LoadingCard />
            <LoadingCard />
            <LoadingCard />
          </>
        ) : topicPreferences.topics.length > 0 ? (
          topicPreferences.topics.map((topic) => (
            <TopicCard key={topic.id} topic={topic} onChange={onTopicPreferenceChange} configurations={topicPreferences.configurations} />
          ))
        ) : (
          <ReactStrap.Col>
            <div className="text-center" style={{ padding: '2rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <span style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}>📰</span>
              <h5 style={{ color: '#6c757d', marginBottom: '0.5rem' }}>No Topics Selected</h5>
              <p className="text-muted mb-0">You haven't subscribed to any topics yet. Browse the recommendations below to personalize your newsletter content.</p>
            </div>
          </ReactStrap.Col>
        )}
      </ReactStrap.Row>
    </div>

    {/* Show Recommended Topics section if there are topics to display OR if still loading */}
    {(topicPreferences.isLoadingSuggestions || topicPreferences.recommendedTopics.length > 0) && (
      <div style={getSectionStyle(topicPreferences.configurations, '#ffc107')}>
        <div style={sectionHeaderStyle}>
          <header className="section-header-grey">Recommended Topics</header>
          <div style={sectionDescriptionStyle}>
            Explore these topics to enhance your content experience:
          </div>
        </div>
        <ReactStrap.Row style={topicsContainerStyle}>
          {topicPreferences.isLoadingSuggestions ? (
            <>
              <LoadingCard />
              <LoadingCard />
              <LoadingCard />
            </>
          ) : (
            topicPreferences.recommendedTopics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} onChange={onTopicPreferenceChange} configurations={topicPreferences.configurations} />
            ))
          )}
        </ReactStrap.Row>
      </div>
    )}

    {/* Add Custom Topics Section - Only show if enabled in configuration */}
    {Utils.isTruthy(Utils.keyValue(topicPreferences.configurations, 'topic_preferences_enable_custom_topics', 'true')) && (
      <div style={getSectionStyle(topicPreferences.configurations, '#17a2b8')}>
        <div style={sectionHeaderStyle}>
          <header className="section-header-grey">Add Custom Topics</header>
          <div style={sectionDescriptionStyle}>
            Can't find what you're looking for? Add your own topics to personalize your newsletter:
          </div>
        </div>

        {/* Display custom topics if any exist */}
        {topicPreferences.customTopics.length > 0 && (
          <ReactStrap.Row style={topicsContainerStyle}>
            {topicPreferences.customTopics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} onChange={onTopicPreferenceChange} configurations={topicPreferences.configurations} />
            ))}
          </ReactStrap.Row>
        )}

        <div style={{
          padding: '1.5rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          marginTop: topicPreferences.customTopics.length > 0 ? '1.5rem' : '0'
        }}>
          <ReactStrap.Label style={{ fontWeight: 500, marginBottom: '0.5rem', color: '#495057' }}>Enter Topics</ReactStrap.Label>
          <ReactStrap.Input
            type="text"
            placeholder="Enter topics separated by commas (e.g., Technology, Sports, Health)"
            style={{ marginBottom: '0.5rem' }}
            onKeyPress={(e: any) => {
              if (e.key === 'Enter') {
                this.handleAddCustomTopics(e.target.value);
                e.target.value = '';
              }
            }}
          />
          <small className="text-muted">
            <strong>Press Enter</strong> to add topics. They will appear above this input field.
          </small>
        </div>
      </div>
    )}

    <div style={saveButtonContainerStyle}>
      {topicPreferences.is_submitting ? (
        <Common.BouncingBalls />
      ) : (
        <ReactStrap.Button
          className='rasa-submit-btn'
          style={{
            ...saveButtonStyle,
            backgroundColor: Utils.keyValue(topicPreferences.configurations, 'topic_preferences_save_button_background_color', '#7FAE1B'),
            color: Utils.keyValue(topicPreferences.configurations, 'topic_preferences_save_button_text_color', '#ffffff'),
          }}
          disabled={!topicPreferences.isDirty}
          onClick={() => {
            // Send:
            // - All user topics (active and inactive for weight updates)
            // - Selected recommended topics (to add as new preferences)
            // - Selected custom topics (to add as new preferences)
            const selectedRecommendedTopics = topicPreferences.recommendedTopics.filter(topic => topic.isActive);
            const selectedCustomTopics = topicPreferences.customTopics.filter(topic => topic.isActive);
            const topicsToSubmit = [...topicPreferences.topics, ...selectedRecommendedTopics, ...selectedCustomTopics];
            submitTopicPreferences(communityIdentifier, guid, topicsToSubmit);
          }}
        >
          {Utils.keyValue(topicPreferences.configurations, 'topic_preferences_save_button_text', 'Save Preferences')}
        </ReactStrap.Button>
      )}
    </div>
  </ReactStrap.Container>
    );
  }
}

const mapStateToProps = ({ topicPreferences }: WrappedTopicPreferences) => ({ topicPreferences });

const mapDispatchToProps = {
  onTopicPreferenceChange: Actions.handleTopicPreferenceChange,
  addCustomTopics: Actions.addCustomTopics,
  submitTopicPreferences: Actions.submitTopicPreferences,
  fetchUserTopics: Actions.fetchUserTopics,
  fetchSuggestedTopics: Actions.fetchSuggestedTopics,
};

export const TopicPreferencesConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(TopicPreferences);